import { Box, Chip, List, ListItem, ListItemText, Skeleton, Typography, Link, Stack, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { OpenInFull as OpenInFullIcon, Close as CloseIcon } from '@mui/icons-material';
import InsightsIcon from '@mui/icons-material/Insights';
import React from 'react';
import { newsApi } from '../../../services/perplexity.js';
import type { WidgetProps } from '../types/index.js';

export const NewsSentimentWidget: React.FC<WidgetProps> = ({ widget }) => {
  const news = widget.inputs.find(i => i.id === 'recentNews')?.value as any[] | undefined;
  const profileId = (widget.config as any)?.profileId as string | undefined;
  const [fetchedNews, setFetchedNews] = React.useState<any[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [showAll, setShowAll] = React.useState(false);
  const [expandedOpen, setExpandedOpen] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    async function fetchIfNeeded() {
      if (news && news.length > 0) return; // inputs provided by profile research
      if (!profileId) return; // no identity to fetch
      try {
        setLoading(true);
        const cfgTicker = (widget.config as any)?.ticker as string | undefined;
        const symbol = cfgTicker || (/^[A-Z.]{1,6}$/.test(profileId) ? profileId : undefined);
        if (!symbol) return;
        const articles = await newsApi.getStockNews(symbol, { profileId, limit: 10 }).catch(() => []);
        if (cancelled) return;
        setFetchedNews(articles || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchIfNeeded();
    return () => {
      cancelled = true;
    };
  }, [news, profileId]);

  const items = news ?? fetchedNews ?? undefined;

  const classify = (s?: string): 'positive' | 'negative' | 'neutral' => {
    const v = String(s || 'neutral').toLowerCase();
    return v.startsWith('pos') ? 'positive' : v.startsWith('neg') ? 'negative' : 'neutral';
  };

  const meta = React.useMemo(() => {
    const counts = { positive: 0, negative: 0, neutral: 0 } as Record<'positive'|'negative'|'neutral', number>;
    (items || []).forEach((n: any) => { counts[classify(n.sentiment)]++; });
    const total = (items || []).length || 1;
    const score = (counts.positive - counts.negative) / total; // -1..1
    const overall = score > 0.15 ? 'Positive' : score < -0.15 ? 'Negative' : 'Neutral';
    return { counts, total, score, overall };
  }, [items]);

  if (!items) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton width={180} />
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} width={'90%'} />
        ))}
      </Box>
    );
  }

  const relativeTime = (d?: string) => {
    if (!d) return '';
    const diffMs = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Meta row */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <InsightsIcon fontSize="small" color="action" />
        <Chip size="small" label={`Overall: ${meta.overall}`} color={meta.overall === 'Positive' ? 'success' : meta.overall === 'Negative' ? 'error' : 'default'} variant="outlined" />
        <Chip size="small" label={`Pos ${meta.counts.positive}`} color="success" variant="outlined" />
        <Chip size="small" label={`Neu ${meta.counts.neutral}`} variant="outlined" />
        <Chip size="small" label={`Neg ${meta.counts.negative}`} color="error" variant="outlined" />
        <Box flexGrow={1} />
        <Tooltip title="Expand">
          <IconButton size="small" onClick={() => setExpandedOpen(true)}>
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {items.length > 8 && (
          <Tooltip title={showAll ? 'Show fewer' : 'Show more'}>
            <IconButton size="small" onClick={() => setShowAll(s => !s)}>
              <Typography variant="caption">{showAll ? 'Less' : 'More'}</Typography>
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      {(items.length === 0 && !loading) ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No recent news</Typography>
      ) : (
        <List dense sx={{ overflow: 'auto' }}>
          {(showAll ? items : items.slice(0, 8)).map((n, idx) => {
            const sentiment: 'pos' | 'neg' | 'neu' = n.sentiment || 'neu';
            const color = sentiment === 'pos' ? 'success.main' : sentiment === 'neg' ? 'error.main' : 'text.secondary';
            return (
              <ListItem key={idx} divider sx={{ alignItems: 'flex-start' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, mt: 0.8, mr: 1 }} />
                <ListItemText
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{n.title}</Typography>
                      <Chip size="small" label={classify(n.sentiment)} variant="outlined" color={classify(n.sentiment) === 'positive' ? 'success' : classify(n.sentiment) === 'negative' ? 'error' : 'default'} />
                      {n.url && (
                        <Link href={n.url} target="_blank" rel="noopener" underline="hover" variant="caption">
                          Open
                        </Link>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">{n.source}</Typography>
                        <Typography variant="caption" color="text.secondary">{relativeTime(n.publishedAt)}</Typography>
                        {n.material && <Chip size="small" variant="outlined" label="material" />}
                      </Box>
                      {n.content && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {n.content}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      )}

      {/* Expanded view */}
      <Dialog open={expandedOpen} onClose={() => setExpandedOpen(false)} fullScreen>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          News Sentiment
          <Chip size="small" label={`Overall: ${meta.overall}`} color={meta.overall === 'Positive' ? 'success' : meta.overall === 'Negative' ? 'error' : 'default'} variant="outlined" sx={{ ml: 1 }} />
          <Chip size="small" label={`Pos ${meta.counts.positive}`} color="success" variant="outlined" />
          <Chip size="small" label={`Neu ${meta.counts.neutral}`} variant="outlined" />
          <Chip size="small" label={`Neg ${meta.counts.negative}`} color="error" variant="outlined" />
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={() => setExpandedOpen(false)} size="small" title="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List dense sx={{ p: 2 }}>
            {items.map((n, idx) => {
              const sentiment: 'pos' | 'neg' | 'neu' = n.sentiment || 'neu';
              const color = sentiment === 'pos' ? 'success.main' : sentiment === 'neg' ? 'error.main' : 'text.secondary';
              return (
                <ListItem key={`expanded-${idx}`} divider sx={{ alignItems: 'flex-start' }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, mt: 0.8, mr: 1 }} />
                <ListItemText
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{n.title}</Typography>
                      <Chip size="small" label={classify(n.sentiment)} variant="outlined" color={classify(n.sentiment) === 'positive' ? 'success' : classify(n.sentiment) === 'negative' ? 'error' : 'default'} />
                      {n.url && (
                          <Link href={n.url} target="_blank" rel="noopener" underline="hover" variant="caption">
                            Open
                          </Link>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">{n.source}</Typography>
                          <Typography variant="caption" color="text.secondary">{relativeTime(n.publishedAt)}</Typography>
                          {n.material && <Chip size="small" variant="outlined" label="material" />}
                        </Box>
                        {n.content && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {n.content}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpandedOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
